import React, { useEffect, useRef } from "react";
import { ScanLine } from "lucide-react";

/**
 * Massive auto-focusing barcode input.
 * Fires onScan(code) when Enter is pressed (USB scanners send Enter).
 * Also fires when input reaches >= 4 chars and remains idle >120ms (fast paste).
 */
export default function BarcodeInput({
  onScan,
  disabled = false,
  placeholder = "Barkodu okutun veya yazın…",
  testId = "scanner-barcode-input",
  autoFocus = true,
}) {
  const ref = useRef(null);
  const flashTimerRef = useRef(null);

  useEffect(() => {
    if (autoFocus && ref.current) ref.current.focus();
  }, [autoFocus]);

  const flash = (kind) => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("scanner-flash-success", "scanner-flash-error");
    // reflow to restart animation
    void el.offsetWidth;
    el.classList.add(
      kind === "ok" ? "scanner-flash-success" : "scanner-flash-error",
    );
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      el.classList.remove("scanner-flash-success", "scanner-flash-error");
    }, 500);
  };

  const submit = () => {
    const val = ref.current?.value?.trim();
    if (!val) return;
    const result = onScan?.(val);
    // consumer can return { ok: false } to trigger error flash
    if (result && result.ok === false) {
      flash("error");
    } else {
      flash("ok");
    }
    ref.current.value = "";
    ref.current.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  // Re-focus when user clicks anywhere except a button/input.
  // Skip attaching entirely when disabled — avoids handling every doc-wide click on other pages.
  useEffect(() => {
    if (disabled) return undefined;
    const handler = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (["input", "textarea", "select", "button", "a", "label"].includes(tag)) return;
      // don't steal focus while a modal/dialog is open
      if (e.target?.closest?.("[role='dialog']")) return;
      ref.current?.focus();
    };
    document.addEventListener("click", handler, { passive: true });
    return () => document.removeEventListener("click", handler);
  }, [disabled]);

  return (
    <div className="relative">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <ScanLine
          className="text-[color:var(--accent)]"
          size={28}
          strokeWidth={2}
        />
      </div>
      <input
        ref={ref}
        type="text"
        disabled={disabled}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        data-testid={testId}
        autoComplete="off"
        spellCheck={false}
        className="scanner-input w-full h-24 md:h-28 pl-16 pr-6 text-2xl md:text-4xl font-bold text-center border-4 border-dashed border-[color:var(--border)] bg-white hard placeholder:text-[color:var(--text-3)] placeholder:text-lg md:placeholder:text-2xl placeholder:font-normal focus:outline-none focus:border-[color:var(--accent)] focus:bg-[color:var(--accent-bg)] disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </div>
  );
}
